import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import { userAppContext } from '../../context/Context.tsx';
import {
  canExtendCommercialTrial,
  DEMO_BOOKED_TAG,
  hasBookedDemo,
  isEligibleForCommercialTrialExtension,
  isTrialExpired,
  mergeTrialAttributes,
} from '../../utils/trialUtils';
import TrialExpiredModal from '../User/TrialExpiredModal.tsx';
import ReviewingApplicationModal from '../User/ReviewingApplicationModal.tsx';
import BookDemoEmailModal from '../NavMenus/BookDemoEmailModal.tsx';
import { approveBookDemoEmail } from '../NavMenus/bookDemoSession';
import AcademicApplicationService from '../../services/AcademicApplicationService.ts';
import { refreshSession } from '../../services/RefreshSession';
import { useAuthStore } from '../../store/authStore';
import { enrichUserWithCognitoAttributes } from '../../utils/enrichUserWithCognitoAttributes';
import { logTrialDebug, logTrialEvaluation } from '../../utils/trialDebug';

const GlobalTrialBanner = () => {
  const { groups, user } = userAppContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('trial');
  const [modalDismissed, setModalDismissed] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  const [academicStatus, setAcademicStatus] = useState(null);
  const [trialType, setTrialType] = useState(null);
  const [trialAttributes, setTrialAttributes] = useState(null);
  const [activeCampaignTags, setActiveCampaignTags] = useState<string[]>([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [hasStartedLoading, setHasStartedLoading] = useState(false);
  const [isBookDemoModalOpen, setIsBookDemoModalOpen] = useState(false);
  const isOnBookDemoPage = location.pathname === '/book-a-demo';
  const shouldSuppressTrialModal = isBookDemoModalOpen || isOnBookDemoPage;

  useEffect(() => {
    if (user && !hasChecked) {
      setModalDismissed(false);
      setHasChecked(true);
    }
  }, [user, hasChecked]);

  const applyAcademicStatusResponse = (response: Awaited<ReturnType<typeof AcademicApplicationService.getAcademicStatus>>) => {
    setAcademicStatus(response.status);
    setTrialType(response.trialType);
    setActiveCampaignTags((response.activeCampaignTags ?? []).map(tag => String(tag)));

    const nextTrialAttributes = {
      createdAt: response.createdAt ?? null,
      extendedTrialDays: response.extendedTrialDays != null ? response.extendedTrialDays : null,
      trialExtendedAt: response.trialExtendedAt ?? null,
    };
    setTrialAttributes(nextTrialAttributes);
    useAuthStore.getState().setTrialAttributes(nextTrialAttributes);
    useAuthStore.getState().setTrialAttributesLoaded(true);
    if (user) {
      useAuthStore.getState().setUser(mergeTrialAttributes(user, nextTrialAttributes));
    }
    if (response.trialType || response.userType) {
      useAuthStore.getState().setUserType(response.trialType || response.userType || null);
    }
  };

  // Check academic status
  useEffect(() => {
    const checkAcademicStatus = async () => {
      if (user?.email && !hasStartedLoading) {
        setHasStartedLoading(true);
        setLoadingStatus(true);
        try {
          const response = await AcademicApplicationService.getAcademicStatus(user.email);

          const contextRoles = groups || [];
          const actualRoles = response.groups || [];

          const roleChanged =
            (actualRoles.includes('ROLE_ACADEMIC') && !contextRoles.includes('ROLE_ACADEMIC')) ||
            (actualRoles.includes('ROLE_STANDARD') && !contextRoles.includes('ROLE_STANDARD')) ||
            (actualRoles.includes('ROLE_ADVANCED') && !contextRoles.includes('ROLE_ADVANCED')) ||
            (contextRoles.includes('ROLE_TRIAL') && !actualRoles.includes('ROLE_TRIAL'));

          if (roleChanged) {
            const reloadKey = `role-reload-${user.email}`;
            const hasReloaded = sessionStorage.getItem(reloadKey);
            const reloadTimestamp = sessionStorage.getItem(`${reloadKey}-timestamp`);

            if (reloadTimestamp) {
              const timeSinceReload = Date.now() - parseInt(reloadTimestamp);
              if (timeSinceReload > 5 * 60 * 1000) {
                sessionStorage.removeItem(reloadKey);
                sessionStorage.removeItem(`${reloadKey}-timestamp`);
              }
            }

            if (!hasReloaded) {
              sessionStorage.setItem(reloadKey, 'true');
              sessionStorage.setItem(`${reloadKey}-timestamp`, Date.now().toString());

              try {
                await refreshSession({ forceRefresh: true });
                window.location.reload();
              } catch (error) {
                console.error('Error refreshing session:', error);
                window.location.reload();
              }
              return;
            } else {
              sessionStorage.removeItem(reloadKey);
              sessionStorage.removeItem(`${reloadKey}-timestamp`);
            }
          }

          applyAcademicStatusResponse(response);

          logTrialDebug('GlobalTrialBanner /academic-status response', {
            email: user.email,
            status: response.status,
            trialType: response.trialType ?? null,
            createdAt: response.createdAt ?? null,
            extendedTrialDays: response.extendedTrialDays ?? null,
            trialExtendedAt: response.trialExtendedAt ?? null,
            hasBookedDemo: hasBookedDemo(response.activeCampaignTags),
            demoBookedTag: DEMO_BOOKED_TAG,
            activeCampaignTags: response.activeCampaignTags ?? [],
          });
        } catch (error) {
          console.error('Error checking academic status:', error);
          setAcademicStatus(null);
          setTrialType(null);
          setActiveCampaignTags([]);
        } finally {
          setLoadingStatus(false);
        }
      }
    };

    if (user?.email) {
      checkAcademicStatus();
    }
  }, [groups, user?.email, hasStartedLoading]);

  useEffect(() => {
    if (!groups?.includes('ROLE_TRIAL') || !user?.email || academicStatus !== 'pending') {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await AcademicApplicationService.getAcademicStatus(user.email);

        if (response.groups?.includes('ROLE_ACADEMIC')) {
          clearInterval(pollInterval);
          window.location.reload();
        }
      } catch (error) {
        console.error('Error polling academic status:', error);
      }
    }, 30000);

    return () => clearInterval(pollInterval);
  }, [groups, user?.email, academicStatus]);

  useEffect(() => {
    if (!groups?.includes('ROLE_TRIAL') || !user?.email || modalType !== 'book-demo-for-extension') {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await AcademicApplicationService.getAcademicStatus(user.email);
        applyAcademicStatusResponse(response);

        if (hasBookedDemo(response.activeCampaignTags)) {
          clearInterval(pollInterval);
          setModalType('extension');
        }
      } catch (error) {
        console.error('Error polling demo booked status:', error);
      }
    }, 30000);

    return () => clearInterval(pollInterval);
  }, [groups, user?.email, modalType]);

  useEffect(() => {
    if (groups?.includes('ROLE_TRIAL') && user?.email) {
      if (!hasStartedLoading || loadingStatus) {
        return;
      }
    }

    if (groups?.includes('ROLE_TRIAL') && user) {
      const dismissKey = 'reviewing-application-dismissed';
      const dismissTimestamp = localStorage.getItem(dismissKey);
      let reviewingModalDismissed = false;

      if (dismissTimestamp) {
        const daysSinceDismiss = (Date.now() - parseInt(dismissTimestamp)) / (1000 * 60 * 60 * 24);
        if (daysSinceDismiss < 7) {
          reviewingModalDismissed = true;
        } else {
          localStorage.removeItem(dismissKey);
        }
      }

      const trialUser = mergeTrialAttributes(user, trialAttributes ?? undefined);
      const expired = isTrialExpired(trialUser);
      const eligibleForExtension = isEligibleForCommercialTrialExtension(trialUser, trialType);
      const canExtend = canExtendCommercialTrial(trialUser, trialType, activeCampaignTags);

      logTrialEvaluation('GlobalTrialBanner modal decision', {
        user,
        trialAttributes,
        trialType,
        extra: {
          pathname: location.pathname,
          academicStatus,
          modalDismissed,
          expired,
          eligibleForExtension,
          canExtend,
          hasBookedDemo: hasBookedDemo(activeCampaignTags),
          activeCampaignTags,
          modalTypeWillBe:
            academicStatus === 'pending' && !reviewingModalDismissed
              ? 'reviewing'
              : expired && !modalDismissed
                ? eligibleForExtension
                  ? canExtend
                    ? 'extension'
                    : 'book-demo-for-extension'
                  : 'trial'
                : 'none',
        },
      });

      if (academicStatus === 'pending' && !reviewingModalDismissed) {
        setModalType('reviewing');
        setTimeout(() => setShowModal(true), 500);
      } else if (expired && !modalDismissed && !shouldSuppressTrialModal) {
        if (eligibleForExtension) {
          setModalType(canExtend ? 'extension' : 'book-demo-for-extension');
        } else {
          setModalType('trial');
        }
        setTimeout(() => setShowModal(true), 500);
      } else {
        setShowModal(false);
      }
    } else {
      setShowModal(false);
    }
  }, [
    groups,
    user,
    modalDismissed,
    academicStatus,
    loadingStatus,
    hasStartedLoading,
    trialType,
    trialAttributes,
    activeCampaignTags,
    location.pathname,
    shouldSuppressTrialModal,
  ]);

  const refreshUserAttributes = async () => {
    const currentUser = await getCurrentUser();
    const session = await refreshSession({ forceRefresh: true });
    const userInfo = session.tokens.idToken.payload;

    const userWithAttributes = await enrichUserWithCognitoAttributes({
      ...userInfo,
      ...currentUser,
    });

    logTrialDebug('GlobalTrialBanner.refreshUserAttributes', {
      jwtAfterForceRefresh: userInfo,
      mergedUser: userWithAttributes,
    });

    useAuthStore.getState().setUser(userWithAttributes);
    return userWithAttributes;
  };

  const handleExtendTrial = async () => {
    if (!user?.email) {
      throw new Error('User email not found');
    }

    const response = await AcademicApplicationService.extendCommercialTrial(user.email);

    if (response.status === 'error') {
      throw new Error(response.message);
    }

    await refreshUserAttributes();
    await useAuthStore.getState().fetchUserType();
    window.location.reload();
  };

  const handleOpenBookDemo = () => {
    setShowModal(false);
    setIsBookDemoModalOpen(true);
  };

  const handleProceedToBookDemo = (email: string) => {
    approveBookDemoEmail(email);
    setIsBookDemoModalOpen(false);
    setShowModal(false);
    navigate('/book-a-demo');
  };

  const handleCloseBookDemoModal = () => {
    setIsBookDemoModalOpen(false);

    if (groups?.includes('ROLE_TRIAL') && user && !modalDismissed && !isOnBookDemoPage) {
      const trialUser = mergeTrialAttributes(user, trialAttributes ?? undefined);
      if (isTrialExpired(trialUser)) {
        setShowModal(true);
      }
    }
  };

  const handleCloseModal = () => {
    if (modalType === 'reviewing') {
      const dismissKey = 'reviewing-application-dismissed';
      localStorage.setItem(dismissKey, Date.now().toString());
    }
    setModalDismissed(true);
    setShowModal(false);
  };

  return (
    <>
      {showModal && modalType === 'reviewing' && <ReviewingApplicationModal onClose={handleCloseModal} />}

      {showModal && modalType !== 'reviewing' && (
        <TrialExpiredModal
          onClose={handleCloseModal}
          variant={
            modalType === 'extension'
              ? 'extension'
              : modalType === 'book-demo-for-extension'
                ? 'book-demo-for-extension'
                : 'expired'
          }
          onExtend={modalType === 'extension' ? handleExtendTrial : undefined}
          onBookDemo={modalType === 'book-demo-for-extension' ? handleOpenBookDemo : undefined}
        />
      )}

      <BookDemoEmailModal
        isOpen={isBookDemoModalOpen}
        onClose={handleCloseBookDemoModal}
        onProceed={handleProceedToBookDemo}
      />
    </>
  );
};

export default GlobalTrialBanner;
